import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { FormField } from '../components/auth/FormField.js';
import { PasswordInput } from '../components/auth/PasswordInput.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { AUTH_STORAGE_KEYS, isValidEmail, loginRequest } from '../lib/auth-api.js';

type AlertState = { variant: 'error' | 'warning'; title?: string; body: string } | null;

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertState>(null);

  const canSubmit = !loading && password.length > 0 && isValidEmail(email);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setAlert(null);
    const result = await loginRequest(email, password);
    if (result.ok) {
      if (result.kind === 'mfa_setup_required') {
        sessionStorage.setItem(AUTH_STORAGE_KEYS.setupToken, result.setup_token);
        navigate('/mfa-setup');
      } else {
        sessionStorage.setItem(AUTH_STORAGE_KEYS.challengeToken, result.challenge_token);
        navigate('/mfa-verify');
      }
      return;
    }
    setLoading(false);
    switch (result.kind) {
      case 'invalid_credentials':
        setAlert({ variant: 'error', body: 'Identifiants invalides.' });
        return;
      case 'account_locked':
        setAlert({
          variant: 'error',
          title: 'Compte temporairement verrouillé',
          body: lockoutBody(result.retryAfterSeconds),
        });
        return;
      case 'rate_limited':
        setAlert({
          variant: 'warning',
          title: 'Trop de tentatives',
          body: rateLimitBody(result.retryAfterSeconds),
        });
        return;
      case 'network_error':
        setAlert({ variant: 'error', body: result.message });
        return;
    }
  }

  return (
    <AuthLayout
      title="Connexion"
      subtitle="Entrez vos identifiants pour accéder au CRM."
      belowCard={
        <p className="text-center text-sm text-ink-secondary">
          Pas encore de compte ? <span className="text-ink">Contactez votre administrateur.</span>
        </p>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {alert ? (
          <div className="mb-4">
            <Alert variant={alert.variant} title={alert.title}>
              {alert.body}
            </Alert>
          </div>
        ) : null}

        <div className="space-y-4">
          <FormField
            label="Adresse e-mail"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vous@exemple.com"
            required
            data-testid="login-email"
          />

          <div>
            <PasswordInput
              label="Mot de passe"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password"
            />
            <div className="mt-2 text-right">
              <Link
                to="/reset-password-request"
                className="btn-ghost !min-h-0 !px-0 !py-0 text-xs text-ink-secondary hover:bg-transparent hover:text-ink"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={loading || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="login-submit"
        >
          {loading ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>
    </AuthLayout>
  );
}

function lockoutBody(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds === null) {
    return 'Trop de tentatives échouées. Réessayez plus tard.';
  }
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return minutes <= 1
    ? 'Trop de tentatives échouées. Réessayez dans environ une minute.'
    : `Trop de tentatives échouées. Réessayez dans environ ${minutes} minutes.`;
}

function rateLimitBody(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds === null) {
    return 'Trop de tentatives. Patientez un instant avant de réessayer.';
  }
  return `Trop de tentatives. Patientez environ ${Math.max(1, retryAfterSeconds)} seconde${
    retryAfterSeconds > 1 ? 's' : ''
  } avant de réessayer.`;
}
